var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { buildPayloadFromState, fetchPrice } from "../api/price";
import { publishPriceResult, syncHiddenForm, } from "../bridge/publishPriceResult";
import { registerOfferCalculator, unregisterOfferCalculator, } from "../bridge/windowBridge";
import { buildSearchParamsFromConfig, pushUrlWithParams, readParamsFromUrl, replaceUrlWithParams, } from "../utils/buildSearchParams";
import { OfferCalculatorDispatchContext, OfferCalculatorStateContext, } from "./OfferCalculatorContext";
import { createInitialState, offerCalculatorReducer, } from "./OfferCalculatorReducer";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, } from "react";
export function OfferCalculatorProvider({ bootstrap, children, }) {
    const [state, dispatch] = useReducer(offerCalculatorReducer, bootstrap, (boot) => createInitialState(boot, readParamsFromUrl()));
    const dispatchWithLeadTracking = useCallback((action) => {
        if (typeof window !== "undefined" && typeof window.$ !== "undefined") {
            if (action.type === "set_param") {
                window.$(document).trigger("lead:change", [action.name]);
            }
            else if (action.type === "set_code") {
                window.$(document).trigger("lead:change", ["code"]);
            }
        }
        dispatch(action);
    }, []);
    const abortRef = useRef(null);
    const stateRef = useRef(state);
    stateRef.current = state;
    const committedCodeRef = useRef(null);
    const runPrice = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        dispatch({ type: "price_request" });
        try {
            const current = stateRef.current;
            const payload = buildPayloadFromState(current.bootstrap, current);
            const response = yield fetchPrice(payload, controller.signal);
            dispatch({ type: "price_success", payload: response });
            const nextState = offerCalculatorReducer(current, {
                type: "price_success",
                payload: response,
            });
            publishPriceResult(response, nextState);
            if (!current.isFirstLoad) {
                const urlParams = buildSearchParamsFromConfig(response.config, response.selected);
                if (committedCodeRef.current !== response.selected) {
                    pushUrlWithParams(urlParams);
                }
                else {
                    replaceUrlWithParams(urlParams);
                }
                committedCodeRef.current = response.selected;
            }
            else {
                committedCodeRef.current = response.selected;
                dispatch({ type: "first_load_done" });
                document.dispatchEvent(new CustomEvent("offer-detail:first-price", { detail: { response } }));
            }
        }
        catch (err) {
            if (!controller.signal.aborted) {
                dispatch({ type: "price_failure" });
            }
        }
    }), []);
    useEffect(() => {
        void runPrice();
    }, [state.changeSeq, runPrice]);
    // The "bridge" is a small imperative API exposed on `window` so legacy
    // (non-React) page scripts can still drive the calculator — read the current
    // code/params, push a new code, or force a recalculation — without going
    // through React. See bridge/windowBridge.ts for the register/unregister glue.
    const bridgeApi = useMemo(() => ({
        setCode: (code) => {
            if (stateRef.current.code !== code) {
                dispatchWithLeadTracking({ type: "set_code", code });
            }
        },
        getCode: () => stateRef.current.code,
        getLastParams: () => stateRef.current.lastParams,
        recalculate: () => {
            void runPrice();
        },
        syncForm: () => {
            syncHiddenForm(stateRef.current);
        },
        resetParams: () => {
            dispatch({ type: "reset_params" });
        },
    }), [runPrice, dispatchWithLeadTracking]);
    // Publish the bridge API on `window` while mounted; tear it down on unmount.
    useEffect(() => {
        registerOfferCalculator(bridgeApi);
        return () => unregisterOfferCalculator();
    }, [bridgeApi]);
    // The version list (rendered outside this React tree) lets the user pick a
    // different car version. When it does, it fires a DOM event with the new code;
    // we forward that into the calculator so a re-price runs for the new version.
    useEffect(() => {
        const onCodeFromVersions = (event) => {
            var _a;
            const code = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.code;
            if (code) {
                bridgeApi.setCode(code);
            }
        };
        document.addEventListener("offer-detail:set-code", onCodeFromVersions);
        return () => document.removeEventListener("offer-detail:set-code", onCodeFromVersions);
    }, [bridgeApi]);
    useEffect(() => {
        const onReset = () => bridgeApi.resetParams();
        document.addEventListener("offer-detail:reset-params", onReset);
        return () => document.removeEventListener("offer-detail:reset-params", onReset);
    }, [bridgeApi]);
    // The standalone packages section (packages.php, rendered outside this tree)
    // can no longer click the old lead-config inputs to toggle a package. It now
    // fires this event; we fold the change into the selected-packages map and
    // re-price, so that block drives calculator state again.
    useEffect(() => {
        const onTogglePackage = (event) => {
            const detail = event
                .detail;
            if (!(detail === null || detail === void 0 ? void 0 : detail.id)) {
                return;
            }
            const next = Object.assign({}, stateRef.current.packages);
            if (detail.checked) {
                next[detail.id] = "1";
            }
            else {
                delete next[detail.id];
            }
            dispatchWithLeadTracking({
                type: "set_param",
                name: "packages",
                value: next,
            });
        };
        document.addEventListener("offer-detail:toggle-package", onTogglePackage);
        return () => document.removeEventListener("offer-detail:toggle-package", onTogglePackage);
    }, [dispatchWithLeadTracking]);
    return (<OfferCalculatorStateContext.Provider value={state}>
      <OfferCalculatorDispatchContext.Provider value={dispatchWithLeadTracking}>
        {children}
      </OfferCalculatorDispatchContext.Provider>
    </OfferCalculatorStateContext.Provider>);
}
